/** Page (relative to the add-in root) rendered inside the weekly-hours dialog. */
export const WEEKLY_HOURS_DIALOG_PAGE = "weekly-hours-dialog.html";

/** Message the dialog page sends via messageParent to ask the task pane to close it. */
export const WEEKLY_HOURS_DIALOG_CLOSE_MESSAGE = "triyo-weekly-hours-dialog-close";

/** displayDialogAsync error code raised when the add-in already has a dialog open. */
const DIALOG_ALREADY_OPEN_CODE = 12007;

/** Dialog size in percent of the Office host window (~the prototype's 520px card). */
const DIALOG_SIZE = { height: 70, width: 35 } as const;

export type WeeklyHoursDisplay = "pane" | "dialog";

/**
 * Resolves the configured "Enter Weekly Hours" surface (`weeklyHoursDisplay`
 * in public/config.js). Defaults to "pane" — the inline task-pane form — when
 * the setting is absent or unrecognized.
 */
export function getWeeklyHoursDisplay(): WeeklyHoursDisplay {
  return window.APP_SETTINGS?.weeklyHoursDisplay === "dialog" ? "dialog" : "pane";
}

let activeDialog: Office.Dialog | null = null;

function closeActiveDialog(): void {
  activeDialog?.close();
  activeDialog = null;
}

/**
 * Opens the weekly-hours page in an Office dialog over the Outlook window,
 * or a popup window outside Office (browser preview). Resolves "opened" while
 * a dialog is showing — including one that was already open — and
 * "unavailable" when nothing could be opened, in which case callers should
 * fall back to the inline task-pane form.
 */
export function openWeeklyHoursDialog(): Promise<"opened" | "unavailable"> {
  const url = new URL(WEEKLY_HOURS_DIALOG_PAGE, window.location.href).toString();

  if (typeof Office !== "undefined" && Office.context?.ui?.displayDialogAsync) {
    return new Promise((resolve) => {
      Office.context.ui.displayDialogAsync(
        url,
        { height: DIALOG_SIZE.height, width: DIALOG_SIZE.width, displayInIframe: true },
        (result) => {
          if (result.status !== Office.AsyncResultStatus.Succeeded) {
            // 12007 fires when the add-in has ANY dialog open (e.g. the SSO
            // web-login dialog); only ours counts as "opened".
            const weeklyHoursDialogAlreadyOpen =
              result.error?.code === DIALOG_ALREADY_OPEN_CODE && activeDialog !== null;
            resolve(weeklyHoursDialogAlreadyOpen ? "opened" : "unavailable");
            return;
          }
          activeDialog = result.value;
          result.value.addEventHandler(
            Office.EventType.DialogMessageReceived,
            (arg: { message: string; origin: string | undefined } | { error: number }) => {
              if ("message" in arg && arg.message === WEEKLY_HOURS_DIALOG_CLOSE_MESSAGE) {
                closeActiveDialog();
              }
            }
          );
          result.value.addEventHandler(Office.EventType.DialogEventReceived, () => {
            activeDialog = null;
          });
          resolve("opened");
        }
      );
    });
  }

  // No "noopener": the page is our own same-origin entry, and with it in the
  // features string window.open returns null even when the popup opens.
  const popup = window.open(url, "_blank", "popup=yes,width=560,height=760");
  return Promise.resolve(popup ? "opened" : "unavailable");
}

/**
 * Called from inside the dialog page: asks the hosting task pane to close this
 * dialog, falling back to window.close() outside Office (browser preview).
 */
export function requestWeeklyHoursDialogClose(): void {
  if (typeof Office !== "undefined" && Office.context?.ui?.messageParent) {
    Office.context.ui.messageParent(WEEKLY_HOURS_DIALOG_CLOSE_MESSAGE);
    return;
  }
  window.close();
}
